import { Component, OnInit, Input, signal, computed, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { IngestionPoint } from "../data-ingestion/data.ingestion.service";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";

@TypeManagerDecorator("workflow-state-rules")
@Component({
    selector: "workflow-state-rules",
    template: `
        <div class="state-rules-container">
            @if (selectedState) {
                <div class="rules-header">
                    <h3>Rules for {{ selectedState.stateName }}</h3>
                </div>

                <div class="form-selector">
                    <label>Form:</label>
                    <div class="form-controls">
                        @if (showFormDropdown()) {
                            <s25-ingestion-point-dropdown
                                [(chosen)]="selectedFormDropdownItem"
                                [enabledOnly]="true"
                                (chosenChange)="onFormSelected($event)"
                                class="form-dropdown"
                            ></s25-ingestion-point-dropdown>
                        }
                        <s25-ng-button
                            type="secondary"
                            buttonText="Refresh"
                            [onClick]="refreshForms"
                            buttonClass="refresh-btn"
                        ></s25-ng-button>
                    </div>
                </div>

                @if (selectedFormId) {
                    <div class="rules-editor">
                        @if (selectedState.workflowStateId) {
                            <s25-ng-rule-tree
                                [category]="'workflowState'"
                                [subCategory]="getStateRuleSubCategory()"
                                [conditionFilterMap]="workflowStateConditionFilterMap()"
                            ></s25-ng-rule-tree>
                        } @else {
                            <div class="unsaved-state-warning">
                                <p>
                                    <strong>State Not Saved:</strong> Please save the workflow process first before
                                    configuring rules for this state.
                                </p>
                            </div>
                        }
                    </div>
                } @else {
                    <div class="no-form-selected">
                        <p>Select a form to configure state rules.</p>
                    </div>
                }
            } @else {
                <div class="no-state">
                    <p>No state selected.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .state-rules-container {
            padding: 20px;
        }

        .rules-header h3 {
            margin: 0 0 16px 0;
            color: #333;
        }

        .form-selector {
            margin-bottom: 20px;
            padding: 16px;
            background: #f8f9fa;
            border-radius: 6px;
            border: 1px solid #e0e0e0;
        }

        .form-selector label {
            display: block;
            font-weight: 500;
            margin-bottom: 8px;
            color: #555;
        }

        .form-controls {
            display: flex;
            gap: 10px;
            align-items: center;
        }

        .form-dropdown {
            flex: 1;
            min-width: 200px;
        }

        .refresh-btn {
            flex-shrink: 0;
        }

        .rules-editor {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            overflow: hidden;
        }

        .no-form-selected,
        .no-state {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }

        .unsaved-state-warning {
            color: #856404;
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 16px;
            text-align: center;
        }

        .unsaved-state-warning p {
            margin: 0;
            font-size: 14px;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25RuleTreeModule, S25ButtonComponent, S25DropdownPaginatedModule],
})
export class WorkflowStateRulesComponent implements OnInit {
    @Input() selectedState: WorkflowTypes.WorkflowState | null = null;
    @Input() workflowProcessId: number | null = null;
    selectedFormId: number | null = null;
    selectedFormDropdownItem: DropDownItem | null = null;
    showFormDropdown = signal(true);

    constructor(private cd: ChangeDetectorRef) {}

    // Condition filter map for workflowState rules - uses ingestion fields for the selected form
    workflowStateConditionFilterMap = computed(() => {
        if (!this.selectedFormId) return {};
        return {
            ingestionFieldsAll: "" + this.selectedFormId,
        };
    });

    ngOnInit() {}

    refreshForms = async () => {
        // Destroy and recreate the dropdown by toggling the @if variable
        this.showFormDropdown.set(false);
        this.cd.detectChanges();
        this.showFormDropdown.set(true);
        this.cd.detectChanges();
    };

    onFormSelected(dropdownItem: DropDownItem | null): void {
        this.selectedFormDropdownItem = dropdownItem;
        this.selectedFormId = dropdownItem?.itemId ? Number(dropdownItem.itemId) : null;
    }

    getSelectedFormName(): string {
        if (!this.selectedFormId) return "";
        const form = this.selectedFormDropdownItem?.data as IngestionPoint;
        return form?.ingestionPointName || `Form ${this.selectedFormId}`;
    }

    getStateRuleSubCategory(): string {
        // For workflow state rules, we need to specify which state we're configuring rules for
        if (!this.selectedFormId || !this.selectedState) return "";
        // Format: stateId & parentItemTypeId & parentItemId
        // For forms, parentItemTypeId is 11_001 (WORKFLOW_INGESTION_POINT_TYPE_ID)
        return `${this.selectedState.workflowStateId}&${INGESTION_POINT_TYPE_ID}&${this.selectedFormId}`;
    }
}
